// ════════════════════════════════════════════════════════════
// useModalStack — Stack global des modals (Dialog + Sheet) ouverts
// ════════════════════════════════════════════════════════════
// Chaque Dialog/Sheet enregistre son ID au mount-open et le retire
// au close. Permet aux overlays de calculer leur opacity progressive
// selon la profondeur du stack, et au modal "le plus bas" de rendre
// l'overlay opaque (les modals au-dessus rendent un overlay invisible
// pour éviter la superposition d'assombrissements en CSS).
//
// Convention :
//   - depth = stack.length (= nombre total de modals ouverts)
//   - index(id) = position dans le stack (0 = le plus bas, à l'arrière)
//   - L'overlay opaque est rendu UNIQUEMENT par le modal d'index 0.
//
// Opacity progressive :
//   - 1 niveau : bg-black/40
//   - 2 niveaux : bg-black/50
//   - 3+ niveaux : bg-black/60 (cap)
// ════════════════════════════════════════════════════════════
import * as React from 'react';
import { create } from 'zustand';

interface ModalStackState {
  stack: string[];
  push: (id: string) => void;
  pop: (id: string) => void;
}

const useModalStackStore = create<ModalStackState>((set) => ({
  stack: [],
  push: (id) => {
    set((s) => (s.stack.includes(id) ? s : { stack: [...s.stack, id] }));
  },
  pop: (id) => {
    set((s) => ({ stack: s.stack.filter((i) => i !== id) }));
  },
}));

let idCounter = 0;
function nextModalId(): string {
  idCounter += 1;
  return `modal-${idCounter}`;
}

/**
 * Enregistre un modal dans le stack global selon son état `open`.
 * Retourne `{ id, depth, index }` :
 *   - `id` — identifiant unique stable (pour debug)
 *   - `depth` — profondeur totale du stack (1, 2, 3…)
 *   - `index` — position dans le stack (0 = le plus bas)
 */
export function useModalStack(open: boolean): {
  id: string;
  depth: number;
  index: number;
} {
  const idRef = React.useRef<string | null>(null);
  idRef.current ??= nextModalId();
  const id = idRef.current;

  const stack = useModalStackStore((s) => s.stack);
  const push = useModalStackStore((s) => s.push);
  const pop = useModalStackStore((s) => s.pop);

  React.useEffect(() => {
    if (open) {
      push(id);
      return () => {
        pop(id);
      };
    }
    return undefined;
  }, [open, id, push, pop]);

  const index = stack.indexOf(id);
  return { id, depth: stack.length, index };
}

/**
 * Classe Tailwind d'opacité du backdrop selon la profondeur du stack.
 * À appliquer SEULEMENT sur l'overlay du modal d'index 0 (les modals
 * supérieurs rendent un overlay transparent).
 */
export function backdropOpacityClass(depth: number): string {
  if (depth <= 1) return 'bg-black/40';
  if (depth === 2) return 'bg-black/50';
  return 'bg-black/60';
}

/** Hook utilitaire — true si le modal courant est au sommet du stack. */
export function useIsTopOfStack(id: string): boolean {
  const stack = useModalStackStore((s) => s.stack);
  return stack.at(-1) === id;
}
